import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Key, Plus, Copy, XCircle, Clock, CheckCircle,
  AlertTriangle, Calendar, Shield
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const PassKeyManager = () => {
  const { apiCall } = useAuth();
  const { toast } = useToast();
  const [passKeys, setPassKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPassKey, setNewPassKey] = useState(null);

  const [formData, setFormData] = useState({
    role_name: '',
    duration_type: '24hours',
    custom_expires_at: '',
    notes: ''
  });

  useEffect(() => {
    fetchPassKeys();
  }, []);

  const fetchPassKeys = async () => {
    try {
      const data = await apiCall('/api/team/pass-keys');
      setPassKeys(data);
    } catch (error) {
      console.error('Failed to fetch pass keys:', error);
    }
  };

  const handleCreatePassKey = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await apiCall('/api/team/pass-keys', {
        method: 'POST',
        body: JSON.stringify({
          ...formData,
          permissions: {} // TODO: Add permission selector
        })
      });

      setNewPassKey(result);
      toast({
        title: 'Pass Key Generated',
        description: 'Copy this key now. It won\'t be shown again.'
      });

      fetchPassKeys();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate pass key',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (!confirm('Are you sure you want to revoke this pass key? This action cannot be undone.')) return;

    try {
      await apiCall(`/api/team/pass-keys/${keyId}/revoke`, {
        method: 'POST'
      });

      toast({
        title: 'Success',
        description: 'Pass key revoked successfully'
      });

      fetchPassKeys();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to revoke pass key',
        variant: 'destructive'
      });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied',
      description: 'Pass key copied to clipboard'
    });
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { color: 'bg-green-500', icon: CheckCircle, text: 'Active' },
      used: { color: 'bg-blue-500', icon: CheckCircle, text: 'Used' },
      expired: { color: 'bg-gray-500', icon: Clock, text: 'Expired' },
      revoked: { color: 'bg-red-500', icon: XCircle, text: 'Revoked' }
    };

    const { color, icon: Icon, text } = config[status] || config.active;

    return (
      <Badge className={`${color} text-white flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {text}
      </Badge>
    );
  };

  const isExpiringSoon = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const hoursLeft = (expiry - now) / (1000 * 60 * 60);
    return hoursLeft > 0 && hoursLeft < 24;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Key className="h-6 w-6" />
            Temporary Access Pass Keys
          </h2>
          <p className="text-gray-600 text-sm mt-1">
            Generate time-limited access keys for third-party collaborators
          </p>
        </div>
        <Button 
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Generate Pass Key
        </Button>
      </div>

      {/* New Pass Key Display */}
      {newPassKey && (
        <Card className="border-2 border-green-500 bg-green-50">
          <CardHeader>
            <CardTitle className="text-green-700">🎉 Pass Key Generated!</CardTitle>
            <CardDescription>
              Copy this key now. For security, it won't be shown again.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 p-4 bg-white border rounded-lg">
              <code className="flex-1 text-lg font-mono font-bold text-blue-600">
                {newPassKey.pass_key}
              </code>
              <Button
                size="sm"
                onClick={() => copyToClipboard(newPassKey.pass_key)}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy
              </Button>
            </div>
            <div className="mt-4 text-sm space-y-1">
              <p><strong>Role:</strong> {newPassKey.role_name}</p>
              <p><strong>Expires:</strong> {new Date(newPassKey.expires_at).toLocaleString()}</p>
              <p className="text-red-600 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Can be used multiple times within the valid timeframe
              </p>
            </div>
            <Button
              className="mt-4"
              variant="outline"
              onClick={() => setNewPassKey(null)}
            >
              Done
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create Modal */}
      {showCreateModal && !newPassKey && (
        <Card className="border-2 border-blue-500">
          <CardHeader>
            <CardTitle>Generate Temporary Pass Key</CardTitle>
            <CardDescription>
              Create a time-limited access key for external collaborators
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreatePassKey} className="space-y-4">
              <div>
                <Label htmlFor="role_name">Access Role / Purpose *</Label>
                <Input
                  id="role_name"
                  value={formData.role_name}
                  onChange={(e) => setFormData({...formData, role_name: e.target.value})}
                  required
                  placeholder="e.g., Accountant View Access, Inventory Manager"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Describe what this key holder can access
                </p>
              </div>

              <div>
                <Label htmlFor="duration">Duration *</Label>
                <Select 
                  value={formData.duration_type}
                  onValueChange={(value) => setFormData({...formData, duration_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1hour">1 Hour</SelectItem>
                    <SelectItem value="24hours">24 Hours</SelectItem>
                    <SelectItem value="7days">7 Days</SelectItem>
                    <SelectItem value="custom">Custom Date/Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.duration_type === 'custom' && (
                <div>
                  <Label htmlFor="custom_expires_at">Expires At *</Label>
                  <Input
                    id="custom_expires_at"
                    type="datetime-local"
                    value={formData.custom_expires_at}
                    onChange={(e) => setFormData({...formData, custom_expires_at: e.target.value})}
                    required
                  />
                </div>
              )}

              <div>
                <Label htmlFor="notes">Notes (optional)</Label>
                <Input
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Additional notes about this access key"
                />
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <strong>Important:</strong> This pass key is single-use and will be consumed on first login. 
                    It will automatically expire at the specified time.
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={loading}>
                  {loading ? 'Generating...' : 'Generate Pass Key'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Pass Keys List */}
      <Card>
        <CardHeader>
          <CardTitle>Generated Pass Keys</CardTitle>
        </CardHeader>
        <CardContent>
          {passKeys.length === 0 ? (
            <div className="text-center py-12">
              <Key className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No pass keys generated yet</p>
              <p className="text-sm text-gray-400">Generate temporary access keys for collaborators</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b bg-gray-50">
                  <tr>
                    <th className="text-left p-3 font-semibold">Pass Key</th>
                    <th className="text-left p-3 font-semibold">Role/Access</th>
                    <th className="text-left p-3 font-semibold">Status</th>
                    <th className="text-left p-3 font-semibold">Expires</th>
                    <th className="text-left p-3 font-semibold">Created</th>
                    <th className="text-left p-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {passKeys.map(key => (
                    <tr key={key.id} className="border-b hover:bg-gray-50">
                      <td className="p-3">
                        <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                          {key.pass_key}
                        </code>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4 text-blue-500" />
                          {key.role_name}
                        </div>
                      </td>
                      <td className="p-3">
                        {getStatusBadge(key.status)}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="h-4 w-4" />
                          {new Date(key.expires_at).toLocaleString()}
                          {isExpiringSoon(key.expires_at) && key.status === 'active' && (
                            <Badge className="bg-yellow-500 text-white ml-2">
                              Expiring Soon
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="h-4 w-4" />
                          {new Date(key.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          {key.status === 'active' && (
                            <>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => copyToClipboard(key.pass_key)}
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => handleRevokeKey(key.id)}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                          {key.status !== 'active' && (
                            <span className="text-sm text-gray-400">No actions</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PassKeyManager;
